import { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Trophy, Users } from "lucide-react";
import { useTournamentProgress } from "@/hooks/useTournamentProgress";
import { useUserGroupInfo } from "@/hooks/useUserGroupInfo";
import { MyGroupView } from "./MyGroupView";
import { AllGroupsView } from "./AllGroupsView";
import { EliminationView } from "./EliminationView";
import { Skeleton } from "@/components/ui/skeleton";

interface MyEvent {
  id: string;
  title: string;
  sport: string;
  startDate: string;
  location: string;
  category: string;
  status: "upcoming" | "active" | "finished";
}

interface TournamentProgressCardProps {
  event: MyEvent;
  userRegistered?: boolean;
  viewMode?: 'full' | 'my-group-only';
}

export function TournamentProgressCard({ event, userRegistered = false, viewMode = 'full' }: TournamentProgressCardProps) {
  const [activeTab, setActiveTab] = useState("my-group");
  const { phase, progress, loading: progressLoading } = useTournamentProgress(event.id);
  const { groupInfo, allGroupInfos, loading: groupLoading } = useUserGroupInfo(event.id);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const activeGroupInfo = allGroupInfos.length > 0 ? allGroupInfos[activeGroupIndex] || allGroupInfos[0] : groupInfo;

  // Get schedule data for all groups
  const [scheduleData, setScheduleData] = useState<any>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const getPhaseLabel = (currentPhase: string) => {
    switch (currentPhase) {
      case 'groups': return 'Fase de Grupos';
      case 'elimination': return 'Fase de Eliminación';
      case 'final': return 'Final';
      case 'finished': return 'Finalizado';
      default: return 'Grupos';
    }
  };

  const getSportIcon = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'tenis':
      case 'tennis':
        return '🎾';
      case 'padel':
      case 'pádel':
        return '🎾';
      case 'pickleball':
        return '🏓';
      default:
        return '🏆';
    }
  };

  if (progressLoading || groupLoading) {
    return (
      <Card className="p-6 space-y-4">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }

  // Vista simplificada para "Mis Eventos" - solo muestra el grupo del usuario
  if (viewMode === 'my-group-only') {
    return (
      <Card className="p-6 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{getSportIcon(event.sport)}</span>
            <h2 className="text-2xl font-bold">{event.title}</h2>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(event.startDate)}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>{event.location}</span>
            </div>
            <Badge variant="secondary">{event.category}</Badge>
          </div>
        </div>

        {/* Sección "Mi Grupo" */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Mi Grupo</h3>
          </div>

          {/* Category selector when multiple registrations */}
          {allGroupInfos.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {allGroupInfos.map((info, idx) => (
                <Badge
                  key={idx}
                  variant={activeGroupIndex === idx ? 'default' : 'outline'}
                  className={`cursor-pointer text-sm px-3 py-1.5 transition-all ${
                    activeGroupIndex === idx 
                      ? 'bg-primary text-primary-foreground shadow-md' 
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => setActiveGroupIndex(idx)}
                >
                  {info.category}{info.subcategory ? ` ${info.subcategory}` : ''} - {info.groupName}
                </Badge>
              ))}
            </div>
          )}
          
          {groupLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : activeGroupInfo ? (
            <MyGroupView groupInfo={activeGroupInfo} />
          ) : (
            <Card className="p-8 text-center space-y-3">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground" />
              <h3 className="font-semibold">Preparando Tu Torneo</h3>
              <p className="text-sm text-muted-foreground">
                Los grupos se asignarán pronto. ¡Mantente atento a las notificaciones!
              </p>
            </Card>
          )}
        </div>
      </Card>
    );
  }

  // Vista completa con tabs (para página de detalles del torneo)
  return (
    <Card className="p-6 space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{getSportIcon(event.sport)}</span>
              <h2 className="text-2xl font-bold">{event.title}</h2>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(event.startDate)}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{event.location}</span>
              </div>
              <Badge variant="secondary">{event.category}</Badge>
            </div>
          </div>
          <Badge variant="default" className="ml-4">
            <Trophy className="w-3 h-3 mr-1" />
            {event.status === 'active' ? 'En Curso' : event.status === 'finished' ? 'Finalizado' : 'Próximo'}
          </Badge>
        </div>

        {/* Progress Bar */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{getPhaseLabel(phase)}</span>
            <span className="text-muted-foreground">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className={`text-center ${progress >= 33 ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              Grupos
            </div>
            <div className={`text-center ${progress >= 66 ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              Eliminación
            </div>
            <div className={`text-center ${progress >= 100 ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
              Final
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full grid grid-cols-3">
          <TabsTrigger value="my-group">Mi Grupo</TabsTrigger>
          <TabsTrigger value="all-groups">Todos los Grupos</TabsTrigger>
          <TabsTrigger value="elimination">Fase de Eliminación</TabsTrigger>
        </TabsList>

        {/* Tab Contents */}
        <TabsContent value="my-group" className="mt-6">
          {/* Category selector when multiple registrations */}
          {allGroupInfos.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {allGroupInfos.map((info, idx) => (
                <Badge
                  key={idx}
                  variant={activeGroupIndex === idx ? 'default' : 'outline'}
                  className={`cursor-pointer text-sm px-3 py-1.5 transition-all ${
                    activeGroupIndex === idx 
                      ? 'bg-primary text-primary-foreground shadow-md' 
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => setActiveGroupIndex(idx)}
                >
                  {info.category}{info.subcategory ? ` ${info.subcategory}` : ''} - {info.groupName}
                </Badge>
              ))}
            </div>
          )}
          {groupLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : activeGroupInfo ? (
            <MyGroupView groupInfo={activeGroupInfo} />
          ) : (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">
                Aún no has sido asignado a un grupo
              </p>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="all-groups" className="mt-6">
          <AllGroupsView eventId={event.id} scheduleData={scheduleData} />
        </TabsContent>

        <TabsContent value="elimination" className="mt-6">
          <EliminationView eventId={event.id} />
        </TabsContent>
      </Tabs>
    </Card>
  );
}
